import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getClientOrderSettings } from 'common/services/OrderService';

import {
    DOCUMENT_TRADE_SUB_TYPE,
    DOCUMENT_TRADE_TYPE,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { DocumentContact, GenericRecord, Quote } from 'common/types';
import { initContact } from 'admin/utils/contact-utils/contact-utils';
import {
    ContactDraftState,
    ContactIdentity,
    createContactDraftReducers,
    isSameContact,
} from 'admin/stores/redux/contactSliceHelpers/contactSliceHelpers';

export interface State extends ContactDraftState {
    initialized: boolean;
    quote: Quote | null;
}

const newDraftContact = () => initContact();

// Stable identity: a fresh [] here re-renders every subscriber on
// every dispatch.
const EMPTY_CONTACTS: DocumentContact[] = [];

const initialState: State = {
    initialized: false,
    quote: null,
    draftContact: newDraftContact(),
};

export const updateClient = createAsyncThunk(
    'orderQuoteCreation/updateClient',
    async ({
        clientId,
        currentClientId,
    }: {
        clientId: number;
        currentClientId: number;
    }) => {
        if (clientId !== currentClientId) {
            const { hasFulfillmentMethods, propertyRequired } =
                await getClientOrderSettings(clientId);

            return {
                clientId,
                hasFulfillmentMethods,
                propertyRequired,
            };
        }

        return null;
    }
);

export const adminOrderQuoteCreation = createSlice({
    name: 'adminOrderQuoteCreation',
    initialState,
    reducers: {
        initialize: (state, action: PayloadAction<Quote>) => {
            state.initialized = true;
            state.quote = action.payload;
            state.draftContact = newDraftContact();
        },

        ...createContactDraftReducers<State>(newDraftContact),

        updateKeyValuePair: (
            state,
            action: PayloadAction<{ key: string | string[]; value: unknown[] }>
        ) => {
            if (!state.quote) {
                return;
            }

            const { key, value } = action.payload;
            const update: GenericRecord<unknown> = {};

            if (Array.isArray(key)) {
                key.forEach((k, idx) => {
                    update[k] = value[idx];
                });
            } else {
                update[key] = value;
            }

            state.quote = { ...state.quote, ...update } as Quote;
        },
        updateSpecialInstructions: (
            state: State,
            action: PayloadAction<string>
        ) => {
            if (!state.quote) {
                return;
            }

            state.quote = {
                ...state.quote,
                instructions: {
                    special: action.payload,
                },
            };
        },
        updateTradeType: (
            state,
            action: PayloadAction<DOCUMENT_TRADE_TYPE>
        ) => {
            if (!state.quote) {
                return;
            }

            const tradeTypeId = action.payload;

            state.quote.tradeTypeId = tradeTypeId;

            if (
                tradeTypeId !== DOCUMENT_TRADE_TYPE.PLUMBING &&
                state.quote.tradeSubTypeId ===
                    DOCUMENT_TRADE_SUB_TYPE.WATERHEATER
            ) {
                state.quote.tradeSubTypeId =
                    DOCUMENT_TRADE_SUB_TYPE.RESIDENTIAL;
            }
        },
        addContact: (state: State, action: PayloadAction<DocumentContact>) => {
            if (!state.quote) {
                return;
            }

            state.quote.contacts = [
                ...(state.quote.contacts ?? []),
                action.payload,
            ];
        },
        removeContact: (
            state: State,
            action: PayloadAction<ContactIdentity>
        ) => {
            if (!state.quote) {
                return;
            }

            state.quote.contacts = state.quote.contacts?.filter(
                contact => !isSameContact(contact, action.payload)
            );
        },
    },
    extraReducers: builder => {
        builder.addCase(updateClient.fulfilled, (state, action) => {
            if (!state.quote || !action.payload) {
                return;
            }

            const { clientId, hasFulfillmentMethods, propertyRequired } =
                action.payload;

            const fulfillmentMethod = hasFulfillmentMethods
                ? state?.quote?.fulfillmentMethod
                : null;

            state.quote = {
                ...state.quote,
                clientId,
                hasFulfillmentMethods,
                fulfillmentMethod,
                propertyRequired,
                propertyDisplay: null,
                propertyId: null,
            };
        });
    },
});

export const {
    initialize,
    updateKeyValuePair,
    updateTradeType,
    addContact,
    removeContact,
    setDraftContact,
    resetDraftContact,
    updateSpecialInstructions,
} = adminOrderQuoteCreation.actions;

interface StateSlice {
    adminOrderQuoteCreation: State;
}

export const orderQuoteCreationSelectedContacts = (state: StateSlice) =>
    state.adminOrderQuoteCreation.quote?.contacts ?? EMPTY_CONTACTS;

export const orderQuoteCreationDraftContact = (state: StateSlice) =>
    state.adminOrderQuoteCreation.draftContact;

export default adminOrderQuoteCreation.reducer;
